import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Layout } from "../components/Layout";
import { TypeBadge, FitBadge, Disclaimer } from "../components/Disclaimer";
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PathPick() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [picking, setPicking] = useState(null);
  const [custom, setCustom] = useState("");

  useEffect(() => {
    api.post("/paths/generate")
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e.response?.data?.detail)));
  }, []);

  const pick = async (p) => {
    setPicking(p.title);
    try {
      const { data: session } = await api.post("/sessions", {
        title: p.title, source: "intake", summary: p.summary, pathData: p,
      });
      toast.success("Path started");
      nav(`/session/${session.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setPicking(null); }
  };

  const startCustom = async () => {
    const title = custom.trim();
    if (!title) return;
    setPicking(title);
    try {
      const { data: session } = await api.post("/sessions", {
        title, source: "custom", summary: `Your own path: ${title}`,
        pathData: { thisWeek: `Take the first small step toward ${title}`, summary: title },
      });
      toast.success("Path started");
      nav(`/session/${session.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setPicking(null); }
  };

  if (err) return (
    <Layout><div className="pm-card p-6 text-center">
      <p className="mb-4">{err}</p>
      <button className="pm-btn" onClick={() => nav("/home")}>Back home</button>
    </div></Layout>
  );

  if (!data) return (
    <Layout><div className="text-center py-20 label-mono" data-testid="paths-loading">Building your local paths…</div></Layout>
  );

  return (
    <Layout>
      <div className="fadeup space-y-6">
        <div>
          <div className="label-mono">Paths for {data.place}</div>
          <h2 className="font-head font-bold text-2xl sm:text-3xl mt-1">Pick one path to start</h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>You run one active path at a time. You can switch later.</p>
        </div>

        <div className="pm-card p-5" data-testid="custom-path-card">
          <h3 className="font-head font-bold text-lg">Have your own path in mind?</h3>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>
            You're not limited to the ideas below — type any path you want to pursue and your mentor will help you do it.
          </p>
          <div className="flex gap-2 mt-3">
            <input className="pm-input text-sm" placeholder="e.g. start a dog-walking service, sell my woodwork online…"
              value={custom} onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && startCustom()} data-testid="custom-path-input" />
            <button onClick={startCustom} disabled={picking || !custom.trim()} className="pm-btn px-4 flex items-center gap-2 whitespace-nowrap" data-testid="custom-path-start">
              {picking === custom.trim() ? "Starting…" : <>Start this <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>

        <div className="grid gap-4" data-testid="paths-list">
          {data.paths?.map((p, i) => (
            <div key={i} className="pm-card p-5" data-testid={`path-card-${i}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <TypeBadge type={p.type} /><FitBadge fit={p.fit} />
              </div>
              <h3 className="font-head font-bold text-lg">{p.title}</h3>
              <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{p.summary}</p>
              <p className="text-sm mt-2"><b>Why this fits:</b> {p.why}</p>
              {p.needs?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.needs.map((n, k) => <span key={k} className="label-mono px-2 py-0.5 border" style={{ borderColor: "var(--border-subtle)" }}>{n}</span>)}
                </div>
              )}
              <div className="mt-3 p-3" style={{ background: "var(--surface-subtle)" }}>
                <div className="label-mono mb-1">This week</div>
                <div className="text-sm">{p.thisWeek}</div>
              </div>
              <button onClick={() => pick(p)} disabled={picking} className="pm-btn mt-4 flex items-center gap-2" data-testid={`pick-path-${i}`}>
                {picking === p.title ? "Starting…" : <>Make this my path <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          ))}
        </div>

        {data.wasteWarnings?.length > 0 && (
          <div className="space-y-3" data-testid="waste-warnings">
            <div className="label-mono flex items-center gap-2" style={{ color: "var(--warning)" }}>
              <AlertTriangle className="w-4 h-4" /> Likely a waste here
            </div>
            {data.wasteWarnings.map((w, i) => (
              <div key={i} className="pm-card p-4" style={{ borderColor: "var(--warning)" }} data-testid={`waste-card-${i}`}>
                <div className="font-semibold">{w.title}</div>
                <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{w.why}</p>
                <p className="text-sm mt-2 flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 mt-0.5" style={{ color: "var(--earn)" }} /> <span><b>Instead:</b> {w.instead}</span></p>
              </div>
            ))}
          </div>
        )}
        <Disclaimer />
      </div>
    </Layout>
  );
}
